import KeyboardRenderer from "./keyboard-renderer";
import { RenderingStatus } from "./types/canvas-renderer";
const DEFAULT_CROP = [0, 0, 0, 0];
export default class MazeRenderer extends KeyboardRenderer {
    constructor() {
        super(...arguments);
        this.keys = new Set();
    }
    reset(data) {
        super.reset(data);
        delete data.direction;
        delete data.moveAction;
        delete data.openAction;
        delete data.position;
        delete data.moveTime;
        delete data.turnTime;
        delete data.openTime;
        delete data.blocks;
    }
    createKeyHandlerUp(data) {
        return (e) => {
            this.keys.delete(e.code);
            delete data.moveAction;
            delete data.openAction;
        };
    }
    createKeyHandlerDown(data) {
        return (e) => {
            e.preventDefault();
            this.keys.add(e.code);
            if (!data.moveAction) {
                data.moveAction = { x: 0, y: 0 };
            }
            switch (e.code) {
                case "KeyW":
                case "ArrowUp":
                    data.moveAction.x = 0;
                    data.moveAction.y = 1;
                    break;
                case "KeyS":
                case "ArrowDown":
                    data.moveAction.x = 0;
                    data.moveAction.y = -1;
                    break;
                case "Space":
                    data.openAction = true;
                    break;
                case "ArrowLeft":
                case "KeyA":
                case "KeyQ":
                    data.moveAction.x = -1;
                    data.moveAction.y = 0;
                    break;
                case "ArrowRight":
                case "KeyD":
                case "KeyE":
                    data.moveAction.x = 1;
                    data.moveAction.y = 0;
                    break;
            }
        };
    }
    saveReturnData(data) {
        var _a, _b, _c, _d;
        return {
            scene: data.title,
            subtitle: data.subtitle,
            position: {
                x: (_b = (_a = data.position) === null || _a === void 0 ? void 0 : _a.x) !== null && _b !== void 0 ? _b : 0,
                y: (_d = (_c = data.position) === null || _c === void 0 ? void 0 : _c.y) !== null && _d !== void 0 ? _d : 0,
            },
        };
    }
    rotationChanged(data) {
        data.blocks = undefined;
    }
    getForwardCoordinates(direction) {
        const forward = { x: 0, y: 0 };
        switch (direction !== null && direction !== void 0 ? direction : 0) {
            case 0:
                forward.y = -1;
                break;
            case 1:
                forward.x = 1;
                break;
            case 2:
                forward.y = 1;
                break;
            case 3:
                forward.x = -1;
                break;
        }
        return forward;
    }
    updateBlocks(data) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        if (data.blocks || !data.position) {
            return data.blocks;
        }
        const forward = this.getForwardCoordinates(data.direction);
        const leftWall = { x: 0, y: 0 };
        switch ((_a = data.direction) !== null && _a !== void 0 ? _a : 0) {
            case 0:
                leftWall.x = -1;
                break;
            case 1:
                leftWall.y = -1;
                break;
            case 2:
                leftWall.x = 1;
                break;
            case 3:
                leftWall.y = 1;
                break;
        }
        const rightWall = { x: -leftWall.x, y: -leftWall.y };
        const blocks = [
            ['', '', '', '', ''],
            ['', '', '', '', ''],
            ['', '', '', '', ''],
            ['', '', '', '', ''],
        ];
        const grid = data.grid;
        for (let level = 0; level < 4; level++) {
            const midBlock = [data.position.x + forward.x * level, data.position.y + forward.y * level];
            const leftBlock = [midBlock[0] + leftWall.x, midBlock[1] + leftWall.y];
            const leftLeftBlock = [midBlock[0] + leftWall.x * 2, midBlock[1] + leftWall.y * 2];
            const rightBlock = [midBlock[0] + rightWall.x, midBlock[1] + rightWall.y];
            const rightRightBlock = [midBlock[0] + rightWall.x * 2, midBlock[1] + rightWall.y * 2];
            blocks[level] = [
                (_c = (_b = grid === null || grid === void 0 ? void 0 : grid[leftLeftBlock[1]]) === null || _b === void 0 ? void 0 : _b[leftLeftBlock[0]]) !== null && _c !== void 0 ? _c : '',
                (_e = (_d = grid === null || grid === void 0 ? void 0 : grid[leftBlock[1]]) === null || _d === void 0 ? void 0 : _d[leftBlock[0]]) !== null && _e !== void 0 ? _e : '',
                (_g = (_f = grid === null || grid === void 0 ? void 0 : grid[midBlock[1]]) === null || _f === void 0 ? void 0 : _f[midBlock[0]]) !== null && _g !== void 0 ? _g : '',
                (_j = (_h = grid === null || grid === void 0 ? void 0 : grid[rightBlock[1]]) === null || _h === void 0 ? void 0 : _h[rightBlock[0]]) !== null && _j !== void 0 ? _j : '',
                (_l = (_k = grid === null || grid === void 0 ? void 0 : grid[rightRightBlock[1]]) === null || _k === void 0 ? void 0 : _k[rightRightBlock[0]]) !== null && _l !== void 0 ? _l : '',
            ];
        }
        data.blocks = blocks;
        return data.blocks;
    }
    drawAnimation(animation, timestamp) {
        var _a, _b, _c;
        if (((_a = animation === null || animation === void 0 ? void 0 : animation.asset) === null || _a === void 0 ? void 0 : _a.image) && this.context) {
            const timeEllapsed = (timestamp !== null && timestamp !== void 0 ? timestamp : 0) - 0;
            const frame = (animation === null || animation === void 0 ? void 0 : animation.frameRate) ? Math.floor(animation.frameRate * timeEllapsed / 1000) : 0;
            const [x, y, width, height, animating] = (_b = animation === null || animation === void 0 ? void 0 : animation.getCrop(frame)) !== null && _b !== void 0 ? _b : DEFAULT_CROP;
            this.context.save();
            if (animation === null || animation === void 0 ? void 0 : animation.flip) {
                this.context.translate(this.context.canvas.width, 0);
                this.context.scale(-1, 1);
            }
            this.context.drawImage((_c = animation === null || animation === void 0 ? void 0 : animation.asset) === null || _c === void 0 ? void 0 : _c.image, x, y, width, height, 0, 0, width, height);
            this.context.restore();
            return { animating };
        }
        return {};
    }
    canMove(data) {
        var _a, _b;
        if (data.destination) {
            return false;
        }
        if (data.moveTime || data.turnTime) {
            return false;
        }
        if (!data.moveAction || !data.position) {
            return false;
        }
        if (data.message) {
            return false;
        }
        const forward = this.getForwardCoordinates(data.direction);
        const mul = data.moveAction.y;
        const cellEnteredCoordinates = { x: forward.x * mul, y: forward.y * mul };
        const gridBlock = (_b = (_a = data.grid) === null || _a === void 0 ? void 0 : _a[data.position.y + cellEnteredCoordinates.y]) === null || _b === void 0 ? void 0 : _b[data.position.x + cellEnteredCoordinates.x];
        return !this.isBlock(gridBlock) && gridBlock !== " $";
    }
    canTurn(data) {
        if (data.destination) {
            return false;
        }
        if (data.message) {
            return false;
        }
        return true;
    }
    canOpen(data) {
        var _a, _b, _c;
        if (data.destination || data.opened) {
            return false;
        }
        if (data.message) {
            return false;
        }
        if (this.isFrontLocked(data)) {
            return (_c = (_b = (_a = data === null || data === void 0 ? void 0 : data.persist) === null || _a === void 0 ? void 0 : _a.game) === null || _b === void 0 ? void 0 : _b.inventory) === null || _c === void 0 ? void 0 : _c.key;
        }
        return this.isFrontDoor(data) || this.isFrontChest(data);
    }
    getFrontPosition(data) {
        var _a, _b, _c, _d;
        const forward = this.getForwardCoordinates(data.direction);
        const cellEnteredCoordinates = { x: forward.x, y: forward.y };
        const x = ((_b = (_a = data.position) === null || _a === void 0 ? void 0 : _a.x) !== null && _b !== void 0 ? _b : 0) + cellEnteredCoordinates.x;
        const y = ((_d = (_c = data.position) === null || _c === void 0 ? void 0 : _c.y) !== null && _d !== void 0 ? _d : 0) + cellEnteredCoordinates.y;
        const tag = `${x}_${y}`;
        return { x, y, tag };
    }
    isFrontDoor(data) {
        var _a, _b, _c, _d;
        if (!data.position) {
            return false;
        }
        const { x, y } = this.getFrontPosition(data);
        return ((_b = (_a = data.grid) === null || _a === void 0 ? void 0 : _a[y]) === null || _b === void 0 ? void 0 : _b[x]) === "DD" || ((_d = (_c = data.grid) === null || _c === void 0 ? void 0 : _c[y]) === null || _d === void 0 ? void 0 : _d[x]) === "LK";
    }
    isFrontLocked(data) {
        var _a, _b;
        if (!data.position) {
            return false;
        }
        const { x, y } = this.getFrontPosition(data);
        return ((_b = (_a = data.grid) === null || _a === void 0 ? void 0 : _a[y]) === null || _b === void 0 ? void 0 : _b[x]) === "LK";
    }
    isFrontStairs(data) {
        var _a, _b;
        if (!data.position) {
            return false;
        }
        const { x, y } = this.getFrontPosition(data);
        return ((_b = (_a = data.grid) === null || _a === void 0 ? void 0 : _a[y]) === null || _b === void 0 ? void 0 : _b[x]) === "EX";
    }
    isFrontChest(data) {
        var _a, _b;
        if (!data.position) {
            return false;
        }
        const { x, y } = this.getFrontPosition(data);
        return ((_b = (_a = data.grid) === null || _a === void 0 ? void 0 : _a[y]) === null || _b === void 0 ? void 0 : _b[x]) === " $";
    }
    isFrontGuard(data) {
        var _a, _b;
        if (!data.position) {
            return false;
        }
        const { x, y } = this.getFrontPosition(data);
        return ((_b = (_a = data.grid) === null || _a === void 0 ? void 0 : _a[y]) === null || _b === void 0 ? void 0 : _b[x]) === " G";
    }
    onChangeCell(data) {
        var _a, _b;
        delete data.blocks;
        delete data.opened;
        const { tag } = this.getFrontPosition(data);
        if (this.isFrontChest(data)) {
            data.opened = this.getProp(data, `chest_${tag}_opened`);
            console.log("Chest", tag);
        }
        const chance = { rand: Math.random() };
        (_b = (_a = data.events) === null || _a === void 0 ? void 0 : _a[tag]) === null || _b === void 0 ? void 0 : _b.forEach(action => {
            this.performAction(data, action, chance);
        });
        this.autoSave(data);
    }
    performRendering(data, timestamp) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        if (!data.position) {
            data.position = { x: 0, y: 0 };
            if (data.returnPosition) {
                data.position.x = data.returnPosition.x;
                data.position.y = data.returnPosition.y;
                data.direction = data.returnDirection;
            }
            else {
                (_a = data.grid) === null || _a === void 0 ? void 0 : _a.forEach((row, gy) => {
                    row.forEach((cell, gx) => {
                        if (cell === " s") {
                            data.position = { x: gx, y: gy };
                        }
                    });
                });
            }
        }
        if (!data.moveTime && !data.turnTime && !data.openTime) {
            if ((_b = data.moveAction) === null || _b === void 0 ? void 0 : _b.y) {
                if (((_c = data.moveAction) === null || _c === void 0 ? void 0 : _c.y) > 0) {
                    if (this.isFrontDoor(data) && data.opened) {
                        data.destination = data.title;
                        data.destinationDirection = data.direction;
                        const forward = this.getForwardCoordinates(data.direction);
                        data.destinationPosition = { x: data.position.x + forward.x * 2, y: data.position.y + forward.y * 2 };
                    }
                    if (this.isFrontStairs(data)) {
                        const { tag } = this.getFrontPosition(data);
                        const dest = (_d = data.portal) === null || _d === void 0 ? void 0 : _d[tag];
                        if (dest) {
                            data.destination = dest.scene;
                            data.destinationPosition = dest.position;
                            data.destinationDirection = dest.direction;
                        }
                        else {
                            console.log("No portal for: ", tag);
                        }
                    }
                }
                if (this.canMove(data)) {
                    data.moveTime = timestamp + 1;
                    data.moveDirection = data.moveAction.y > 0 ? "FORWARD" : "BACKWARD";
                    if (data.moveDirection === "BACKWARD") {
                        const forward = this.getForwardCoordinates(data.direction);
                        data.position.x -= forward.x;
                        data.position.y -= forward.y;
                        this.onChangeCell(data);
                    }
                }
            }
            if ((_e = data.moveAction) === null || _e === void 0 ? void 0 : _e.x) {
                if (this.canTurn(data)) {
                    data.turnTime = timestamp + 1;
                    data.turn = data.moveAction.x < 0 ? "LEFT" : "RIGHT";
                }
            }
            if (data.openAction) {
                if (this.canOpen(data)) {
                    data.openTime = timestamp + 1;
                }
                else if (this.isFrontLocked(data) && !data.opened) {
                    data.message = "The door is locked.";
                }
            }
        }
        let moveIndex = 0;
        if (data.moveTime) {
            const moveTimeProgress = Math.ceil((timestamp - data.moveTime) / ((_f = data.moveDuration) !== null && _f !== void 0 ? _f : 100));
            if (data.moveDirection === "FORWARD") {
                if (moveTimeProgress < 3) {
                    moveIndex = moveTimeProgress;
                }
                else {
                    delete data.moveTime;
                    const forward = this.getForwardCoordinates(data.direction);
                    data.position.x += forward.x;
                    data.position.y += forward.y;
                    this.onChangeCell(data);
                }
            }
            else {
                if (moveTimeProgress < 3) {
                    moveIndex = 3 - moveTimeProgress;
                }
                else {
                    delete data.moveTime;
                }
            }
        }
        let turnIndex = 0;
        if (data.turnTime) {
            const turnTimeProgress = Math.ceil((timestamp - data.turnTime) / ((_g = data.turnDuration) !== null && _g !== void 0 ? _g : 150));
            if (turnTimeProgress < 2) {
                turnIndex = turnTimeProgress;
            }
            else {
                data.direction = (((_h = data.direction) !== null && _h !== void 0 ? _h : 0) + (data.turn === "LEFT" ? -1 : 1) + 4) % 4;
                delete data.blocks;
                delete data.turnTime;
            }
        }
        let openIndex = data.opened ? 2 : 0;
        if (data.openTime) {
            const openTimeProgress = Math.ceil((timestamp - data.openTime) / ((_j = data.openDuration) !== null && _j !== void 0 ? _j : 100));
            if (openTimeProgress < 2) {
                openIndex = openTimeProgress;
            }
            else {
                delete data.openTime;
                data.opened = true;
                openIndex = 2;
                this.playSFX((_k = data.sounds) === null || _k === void 0 ? void 0 : _k.door);
                if (this.isFrontChest(data)) {
                    const { tag } = this.getFrontPosition(data);
                    this.saveProp(data, `chest_${tag}_opened`, true);
                    this.pickItem(data, (_l = data.treasures) === null || _l === void 0 ? void 0 : _l[tag]);
                }
            }
        }
        this.updateBlocks(data);
        this.drawBackground(data, timestamp, moveIndex, turnIndex);
        this.drawBlocks(data, timestamp, moveIndex, turnIndex, openIndex);
        return data.destination && !data.message ? RenderingStatus.COMPLETED : RenderingStatus.RENDERING;
    }
    drawBackground(data, timestamp, moveIndex, turnIndex) {
        var _a, _b;
        if (data.turnTime) {
            this.drawAnimation((_a = data.sprites) === null || _a === void 0 ? void 0 : _a.background.rotate, timestamp);
        }
        else {
            this.drawAnimation((_b = data.sprites) === null || _b === void 0 ? void 0 : _b.background.move[moveIndex], timestamp);
        }
    }
    isBlock(block) {
        return block === "[]" || block === "EX" || block === "DD" || block === "LK";
    }
    isExit(block) {
        return block === "EX" || block === "DD" || block === "LK";
    }
    isChest(block) {
        return block === " $";
    }
    isGuard(block) {
        return block === " G";
    }
    drawBlocks(data, timestamp, moveIndex, turnIndex, openIndex) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11;
        const blocks = data.blocks;
        if (blocks === null || blocks === void 0 ? void 0 : blocks.length) {
            if (data.turnTime) {
                const mid = 2;
                const frontXY = { x: data.turn === "LEFT" ? mid - 1 : mid + 1, y: 1 };
                const frontBlock = blocks[frontXY.y][frontXY.x];
                const leftBlock = data.turn === "LEFT" ? blocks[0][mid - 1] : blocks[1][mid];
                const rightBlock = data.turn === "RIGHT" ? blocks[0][mid + 1] : blocks[1][mid];
                const farLeftBlock = data.turn === "LEFT" ? blocks[frontXY.y][frontXY.x - 1] : blocks[frontXY.y + 1][frontXY.x];
                const farRightBlock = data.turn === "RIGHT" ? blocks[frontXY.y][frontXY.x + 1] : blocks[frontXY.y + 1][frontXY.x];
                if (this.isBlock(farLeftBlock)) {
                    this.drawAnimation((_b = (_a = data.sprites) === null || _a === void 0 ? void 0 : _a.corners) === null || _b === void 0 ? void 0 : _b.far.left, timestamp);
                }
                if (this.isBlock(farRightBlock)) {
                    this.drawAnimation((_d = (_c = data.sprites) === null || _c === void 0 ? void 0 : _c.corners) === null || _d === void 0 ? void 0 : _d.far.right, timestamp);
                }
                if (this.isBlock(frontBlock)) {
                    this.drawAnimation((_f = (_e = data.sprites) === null || _e === void 0 ? void 0 : _e.corners) === null || _f === void 0 ? void 0 : _f.mid, timestamp);
                }
                if (this.isBlock(leftBlock)) {
                    this.drawAnimation((_h = (_g = data.sprites) === null || _g === void 0 ? void 0 : _g.corners) === null || _h === void 0 ? void 0 : _h.close.left, timestamp);
                }
                if (this.isBlock(rightBlock)) {
                    this.drawAnimation((_k = (_j = data.sprites) === null || _j === void 0 ? void 0 : _j.corners) === null || _k === void 0 ? void 0 : _k.close.right, timestamp);
                }
            }
            else {
                for (let i = (blocks === null || blocks === void 0 ? void 0 : blocks.length) - 1; i >= 0; i--) {
                    const [leftLeft, leftBlock, midBlock, rightBlock, rightRight] = blocks[i];
                    if (this.isBlock(leftBlock)) {
                        this.drawAnimation((_p = (_o = (_m = (_l = data.sprites) === null || _l === void 0 ? void 0 : _l.walls) === null || _m === void 0 ? void 0 : _m.left) === null || _o === void 0 ? void 0 : _o[i]) === null || _p === void 0 ? void 0 : _p[moveIndex], timestamp);
                    }
                    if (this.isBlock(rightBlock)) {
                        this.drawAnimation((_t = (_s = (_r = (_q = data.sprites) === null || _q === void 0 ? void 0 : _q.walls) === null || _r === void 0 ? void 0 : _r.right) === null || _s === void 0 ? void 0 : _s[i]) === null || _t === void 0 ? void 0 : _t[moveIndex], timestamp);
                    }
                    if (this.isBlock(midBlock)) {
                        this.drawAnimation((_x = (_w = (_v = (_u = data.sprites) === null || _u === void 0 ? void 0 : _u.walls) === null || _v === void 0 ? void 0 : _v.face) === null || _w === void 0 ? void 0 : _w[i]) === null || _x === void 0 ? void 0 : _x[moveIndex], timestamp);
                        if (this.isExit(midBlock)) {
                            this.drawAnimation((_1 = (_0 = (_z = (_y = data.sprites) === null || _y === void 0 ? void 0 : _y.walls) === null || _z === void 0 ? void 0 : _z.exit) === null || _0 === void 0 ? void 0 : _0[i]) === null || _1 === void 0 ? void 0 : _1[moveIndex], timestamp);
                        }
                    }
                    if (i === 1 && moveIndex === 0) {
                        if (midBlock === "EX") {
                            this.drawAnimation((_3 = (_2 = data.sprites) === null || _2 === void 0 ? void 0 : _2.walls) === null || _3 === void 0 ? void 0 : _3.stairs, timestamp);
                        }
                        else if (midBlock === "DD") {
                            this.drawAnimation((_5 = (_4 = data.sprites) === null || _4 === void 0 ? void 0 : _4.walls) === null || _5 === void 0 ? void 0 : _5.door[openIndex], timestamp);
                        }
                        else if (midBlock === "LK") {
                            this.drawAnimation((_7 = (_6 = data.sprites) === null || _6 === void 0 ? void 0 : _6.walls) === null || _7 === void 0 ? void 0 : _7.lock[openIndex], timestamp);
                        }
                        else if (this.isChest(midBlock)) {
                            this.drawAnimation((_9 = (_8 = data.sprites) === null || _8 === void 0 ? void 0 : _8.walls) === null || _9 === void 0 ? void 0 : _9.chest[openIndex], timestamp);
                        }
                        else if (this.isGuard(midBlock)) {
                            this.drawAnimation((_11 = (_10 = data.sprites) === null || _10 === void 0 ? void 0 : _10.walls) === null || _11 === void 0 ? void 0 : _11.guard, timestamp);
                        }
                    }
                }
            }
        }
    }
    getConnectionTag(data) {
        var _a;
        if (!data.position) {
            return;
        }
        const { x, y } = data.position;
        const forward = this.getForwardCoordinates(data.direction);
        return `${data.title}|${(_a = data.subtitle) !== null && _a !== void 0 ? _a : ''}|${x + forward.x},${y + forward.y}`;
    }
}
